import { useTranslation } from 'react-i18next';
import { useAccessLevel, useFeatureFlags, usePermissions } from 'core/app/AppContext';
import { FileManagementRoute } from 'sections/file-management/routes';
import { AuthorisedMainNavLink } from './Auth/AuthorisedMainNavLink';
import { toggleSelectClientModal, usePreferencesDispatch } from 'core/preferences/usePreferencesContext';
import { MainNavButton } from './MainNavButton';
interface MainNavProps {
  clientId: string;
  level: string;
}

const MainNavLinks = ({ clientId, level }: MainNavProps) => {
  const { t } = useTranslation();
  const { accessLevel } = useAccessLevel();
  const { hasPermission } = usePermissions();
  const { flagEnabled } = useFeatureFlags();
  const dispatch = usePreferencesDispatch();

  const isTopLevelUrl = !clientId;
  const isSiteLevel = level === 'sites';
  const isBrandLevel = level === 'brands';
  const hasClient = !!(level && clientId);

  /*
   Item 'to' prop is not only a push/link/route it is also
   the route that the icon will be active on (bg filled) not exact unless specified.
  */
  const renderDashboardLink = () => {
    let renderDashboardLink = false;

    if (isTopLevelUrl || isSiteLevel) renderDashboardLink = true;
    else if (isBrandLevel && flagEnabled('mps-dashboard-brands')) renderDashboardLink = true;

    if (renderDashboardLink) {
      return (
        <AuthorisedMainNavLink
          permissions={['ViewDashboards']}
          to={hasClient ? `/${level}/${clientId}/dashboard` : '/'}
          exact={true}
          icon="chart-line"
          tooltipDescription={t('mainnav.dashboard')}
        />
      );
    }
  };

  const renderCampaignsLink = () => {
    if (hasClient && isSiteLevel) {
      return (
        <AuthorisedMainNavLink
          permissions={['ManageCampaigns']}
          to={`/${level}/${clientId}/campaigns`}
          icon="rocket"
          tooltipDescription={t('mainnav.campaigns')}
        />
      );
    } else if (accessLevel === 'SaleCycle' && isTopLevelUrl && flagEnabled('top-level-campaigns-navbutton')) {
      return (
        <MainNavButton
          icon="rocket"
          onClick={() => toggleSelectClientModal(dispatch, true)}
          tooltipDescription={t('mainnav.campaigns')}
        />
      );
    }
  };

  /*
  Has some particularly ugly logic where the main nav either appears if you have
  ManageAssets or ManageTemplates need to look at this in future.
  */
  const renderDesignsLink = () => {
    const canUseTemplates = hasPermission('ManageTemplates');
    if (hasClient && isSiteLevel && (canUseTemplates || hasPermission('ManageAssets'))) {
      return (
        <AuthorisedMainNavLink
          permissions={[]}
          to={`/sites/${clientId}/designs`}
          icon="paint-brush"
          tooltipDescription={t('mainnav.designs')}
        />
      );
    }

    if (accessLevel === 'SaleCycle' && isTopLevelUrl) {
      return (
        <AuthorisedMainNavLink
          permissions={['ManageAssets']}
          to="/designs"
          icon="paint-brush"
          tooltipDescription={t('mainnav.designs')}
        />
      );
    }
  };

  const renderUserManagementLink = () => {
    if (accessLevel === 'SaleCycle' && isTopLevelUrl) {
      return (
        <AuthorisedMainNavLink
          permissions={['ManageUsers', 'ManageSftpUsers']}
          atLeastOnePermission={true}
          to="/user-management"
          icon="users"
          tooltipDescription={t('mainnav.userManagement')}
        />
      );
    }
  };

  const renderFileManagementLink = () => {
    if (isTopLevelUrl) {
      return (
        <AuthorisedMainNavLink
          permissions={['ManageClients']}
          to={FileManagementRoute.HOME}
          icon="folder-tree"
          tooltipDescription={t('mainnav.fileManagement')}
        />
      );
    }
  };

  const renderSettingsLink = () => {
    if (hasClient) {
      return (
        <AuthorisedMainNavLink
          permissions={['ManageClients']}
          to={`/${level}/${clientId}/settings`}
          icon="cog"
          tooltipDescription={t('settings.label')}
          testId="MainSettingsLink"
        />
      );
    }
  };

  const renderComplianceLink = () => {
    if ((flagEnabled('compliance-send-rules') || flagEnabled('compliance-consent-history')) && isSiteLevel) {
      return (
        <AuthorisedMainNavLink
          permissions={['ManageClients']}
          to={`/${level}/${clientId}/compliance`}
          icon="check-circle"
          tooltipDescription={t('mainnav.compliance')}
        />
      );
    }
  };

  return (
    <>
      {renderDashboardLink()}
      {renderCampaignsLink()}
      {renderDesignsLink()}
      {renderUserManagementLink()}
      {renderFileManagementLink()}
      {renderComplianceLink()}
      {renderSettingsLink()}
    </>
  );
};

export default MainNavLinks;
